// Trend Chart — PSD §7.4 (F7.4). Dashboard.
//
// Stage 7 /delta remediation: replaced the hand-rolled fixed-viewBox SVG with
// a responsive `recharts` LineChart. Preserves the same data shape
// ({ date, fmv, strike }) and the dashed-strike / solid-FMV semantics, adds a
// hover tooltip for exact per-valuation values, and re-applies the canonical
// `--color-accent` / `--color-positive` / `--color-text-muted` tokens.

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { usdPrecise } from '../../format/format';

interface TrendChartProps {
  data: { date: string; fmv: number; strike: number }[];
}

interface TrendPoint {
  date: string;
  fmv: number;
  strike: number;
}

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: TrendPoint }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="trend-tooltip">
      <div className="trend-tooltip-date">{p.date.slice(0, 10)}</div>
      <div className="trend-tooltip-row">
        <span className="trend-dot trend-dot-fmv" aria-hidden="true" />
        <span>FMV</span>
        <strong>{usdPrecise(p.fmv)}</strong>
      </div>
      <div className="trend-tooltip-row">
        <span className="trend-dot trend-dot-strike" aria-hidden="true" />
        <span>Strike</span>
        <strong>{usdPrecise(p.strike)}</strong>
      </div>
    </div>
  );
}

export function TrendChart({ data }: TrendChartProps) {
  if (data.length < 2) {
    return (
      <div className="card">
        <h3>FMV Trend</h3>
        <p className="field-hint">Finalize at least 2 valuations to see the trend.</p>
      </div>
    );
  }

  // Normalize date to YYYY-MM-DD for the axis label and stable sort.
  const chartData = data.map((d) => ({ ...d, date: d.date.slice(0, 10) }));

  return (
    <div className="card trend-chart">
      <h3>FMV &amp; Strike Trend</h3>
      <p className="result-caption">
        Plots common FMV per share (solid) against the recommended option strike
        (dashed, rounded up to the nearest cent) across finalized valuations for
        this entity. Values in USD. Hover any point for exact figures.
      </p>
      <div className="trend-chart-container" role="img" aria-label="FMV and strike price trend chart">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--color-border)" strokeDasharray="2 3" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--color-text-muted)"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
            />
            <YAxis
              stroke="var(--color-text-muted)"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickFormatter={(v: number) => `$${v.toFixed(2)}`}
              width={56}
            />
            <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'var(--color-border-strong)' }} />
            <Legend
              iconType="line"
              formatter={(value: string) => <span className="trend-legend-label">{value}</span>}
            />
            <Line
              type="monotone"
              dataKey="fmv"
              name="FMV"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={{ r: 3, fill: 'var(--color-accent)', strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="strike"
              name="Strike"
              stroke="var(--color-positive)"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 3, fill: 'var(--color-positive)', strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
